// G3b — Öffentliches Anfrageformular (kein Login erforderlich).
// Ruft GET /api/intake/:token via plain fetch (kein api()-Wrapper, kein useMe).

import { useEffect, useRef, useState } from "preact/hooks";

interface IntakeField {
  key: string;
  label: string;
  type: "text" | "textarea" | "email" | "number";
  required: boolean;
  options?: string[];
}

interface IntakeFormData {
  name: string;
  fields: IntakeField[];
}

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "form"; data: IntakeFormData }
  | { status: "success" };

type SubmitState = "idle" | "submitting" | "error" | "rateLimit";

export function IntakeFormPage({ params }: { params: { token: string } }): preact.JSX.Element {
  const token = params.token;
  const [page, setPage] = useState<PageState>({ status: "loading" });
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  // Honeypot ref — visually hidden, must remain empty for real users.
  const honeypotRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void fetch("/api/intake/" + encodeURIComponent(token))
      .then(async (res) => {
        if (res.status === 404) {
          setPage({ status: "error", message: "Formular nicht gefunden oder deaktiviert." });
          return;
        }
        if (!res.ok) {
          setPage({ status: "error", message: "Formular nicht gefunden oder deaktiviert." });
          return;
        }
        const data = (await res.json()) as IntakeFormData;
        // Pre-populate values map with empty strings for all fields.
        const initial: Record<string, string> = {};
        for (const f of data.fields) {
          initial[f.key] = "";
        }
        setValues(initial);
        setPage({ status: "form", data });
      })
      .catch(() => {
        setPage({ status: "error", message: "Formular nicht gefunden oder deaktiviert." });
      });
  }, [token]);

  function validate(fields: IntakeField[]): boolean {
    const errors: Record<string, string> = {};
    for (const f of fields) {
      if (f.required && !(values[f.key] ?? "").trim()) {
        errors[f.key] = "Dieses Feld ist erforderlich.";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleChange(key: string, value: string): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear error on change.
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  async function handleSubmit(e: Event, fields: IntakeField[]): Promise<void> {
    e.preventDefault();
    if (!validate(fields)) return;

    setSubmitState("submitting");
    setSubmitError("");

    const body: Record<string, string> = { ...values };
    // Include honeypot — real users leave it empty; bots filling it are silently accepted by server.
    body["_hp"] = honeypotRef.current?.value ?? "";

    try {
      const res = await fetch("/api/intake/" + encodeURIComponent(token), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean };
        if (data.ok) {
          setPage({ status: "success" });
          return;
        }
      }
      if (res.status === 400) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setSubmitError(data.error ?? "Bitte prüfen Sie Ihre Angaben.");
        setSubmitState("error");
        return;
      }
      if (res.status === 429) {
        setSubmitState("rateLimit");
        return;
      }
      setSubmitError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      setSubmitState("error");
    } catch {
      setSubmitError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      setSubmitState("error");
    }
  }

  return (
    <main class="min-h-dvh flex items-center justify-center p-6 bg-[color:var(--color-surface)]">
      <div class="max-w-lg w-full rounded-lg bg-[color:var(--color-surface-container-lowest)] shadow-level-2 border border-[color:var(--color-outline-variant)] p-4 sm:p-6">
        {page.status === "loading" && (
          <p class="text-sm text-center text-[color:var(--color-on-surface-variant)]">
            Formular wird geladen…
          </p>
        )}

        {page.status === "error" && (
          <div class="text-center">
            <h1 class="text-2xl font-bold text-[color:var(--color-on-surface)] mb-3">juuplan</h1>
            <p class="text-sm text-[color:var(--color-error)]">{page.message}</p>
          </div>
        )}

        {page.status === "success" && (
          <div class="text-center">
            <h1 class="text-2xl font-bold text-[color:var(--color-on-surface)] mb-3">Vielen Dank!</h1>
            <p class="text-sm text-[color:var(--color-on-surface-variant)]">
              Ihre Anfrage wurde erfolgreich übermittelt. Wir melden uns in Kürze bei Ihnen.
            </p>
          </div>
        )}

        {page.status === "form" && (
          <>
            <header class="mb-6">
              <h1 class="text-2xl font-bold text-[color:var(--color-on-surface)]">{page.data.name}</h1>
              <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
                Felder mit <span class="text-[color:var(--color-error)]">*</span> sind Pflichtfelder.
              </p>
            </header>

            <form
              onSubmit={(e) => {
                void handleSubmit(e, page.data.fields);
              }}
              noValidate
            >
              {/* Honeypot: visually hidden, not visible to real users; sent as _hp in submission. */}
              <div
                aria-hidden="true"
                style="position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;"
              >
                <label for="intake_hp">Website</label>
                <input
                  id="intake_hp"
                  type="text"
                  name="_hp"
                  ref={honeypotRef}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div class="space-y-4">
                {page.data.fields.map((field) => (
                  <div key={field.key}>
                    <label
                      for={`field_${field.key}`}
                      class="block text-sm font-medium text-[color:var(--color-on-surface)] mb-1"
                    >
                      {field.label}
                      {field.required && (
                        <span class="ml-1 text-[color:var(--color-error)]" aria-hidden="true">*</span>
                      )}
                    </label>

                    {field.type === "textarea" ? (
                      <textarea
                        id={`field_${field.key}`}
                        name={field.key}
                        required={field.required}
                        rows={4}
                        value={values[field.key] ?? ""}
                        onInput={(e) => {
                          handleChange(field.key, (e.target as HTMLTextAreaElement).value);
                        }}
                        class="w-full rounded border border-[color:var(--color-outline)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] resize-y"
                      />
                    ) : (
                      <input
                        id={`field_${field.key}`}
                        name={field.key}
                        type={field.type}
                        required={field.required}
                        value={values[field.key] ?? ""}
                        onInput={(e) => {
                          handleChange(field.key, (e.target as HTMLInputElement).value);
                        }}
                        class="w-full rounded border border-[color:var(--color-outline)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                      />
                    )}

                    {validationErrors[field.key] && (
                      <p class="mt-1 text-xs text-[color:var(--color-error)]">
                        {validationErrors[field.key]}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {submitState === "rateLimit" && (
                <p class="mt-4 text-sm text-[color:var(--color-error)]">
                  Zu viele Anfragen, bitte später erneut versuchen.
                </p>
              )}
              {submitState === "error" && submitError && (
                <p class="mt-4 text-sm text-[color:var(--color-error)]">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={submitState === "submitting"}
                class="mt-6 w-full inline-flex items-center justify-center rounded bg-[color:var(--color-primary)] px-4 py-3 text-sm font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-surface-container-lowest)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitState === "submitting" ? "Wird gesendet…" : "Anfrage absenden"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
